import classNames from 'classnames';
import React, { HTMLAttributes } from 'react';

import countryCodesAlpha3 from '~/utils/country-iso-codes/country-iso-alpha-three.json';
import countryCodes from '~/utils/country-iso-codes/country-iso-codes.json';
import regionalCodes from '~/utils/country-iso-codes/regional-codes.json';

import * as S from './FlagIcon.styles';
import { convertCountryCodeToAlpha2, isRegionalCode } from './utils';

export interface FlagIconProps extends HTMLAttributes<HTMLSpanElement> {
  countryCode?: string;
  variant?: 'default' | 'rounded' | 'circular';
  customSize?: number;
}

export const FlagIcon = ({
  countryCode = '',
  variant = 'default',
  customSize = 14, // Default to 14px (equivalent to previous 'md' size)
  ...rest
}: FlagIconProps) => {
  const countryCodeUpperCase = countryCode.toUpperCase();

  if (!countryCode) {
    return null;
  }

  const isValidCode =
    countryCodesAlpha3.includes(countryCodeUpperCase) ||
    countryCodes.includes(countryCodeUpperCase) ||
    regionalCodes.includes(countryCodeUpperCase);

  if (!isValidCode) {
    return (
      <S.Fallback $customSize={customSize} data-testid="fallback" {...rest}>
        {countryCodeUpperCase}
      </S.Fallback>
    );
  }

  const alpha2Code = convertCountryCodeToAlpha2(countryCodeUpperCase);

  if (!alpha2Code || isRegionalCode(countryCodeUpperCase)) {
    return (
      <S.Fallback $customSize={customSize} data-testid="fallback" {...rest}>
        {countryCodeUpperCase}
      </S.Fallback>
    );
  }

  const { className, ...otherProps } = rest;

  const flagClasses = classNames(
    'fi',
    `fi-${alpha2Code.toLowerCase()}`,
    variant === 'circular' && 'fis',
    className
  );

  return (
    <S.Flag
      $variant={variant}
      $customSize={customSize}
      className={flagClasses}
      data-country={countryCodeUpperCase}
      {...otherProps}
    />
  );
};
